import React, { useState } from 'react';
import {
  Box,
  Button,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfPlaceholder } from '@qualifio/ui-commons';
import { RuleType } from '@loyalty/shared-common/rule-api-client';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';
import { useTranslation } from 'react-i18next';
import { BurningRuleModal } from './BurningRuleModal';
import { BurningRuleTableRow } from './BurningRuleTableRow';
import { DetailForm } from '../shared/layout/DetailForm';
import { QlfPlaceholderIcon } from '../shared/qlf-placeholder-icon/QlfPlaceholderIcon';
import { RuleConflictModal, RuleConflictModalProps } from '../rules/components/RuleConflictModal';
import { RuleImportExportButtons } from '../rules/components/import-export/RuleImportExportButtons';
import { RulesFilters } from '../rules/components/RulesFilters';
import { burningRulesIcon } from '../shared/icons';
import { moveRule } from '../rules/utils';
import { ruleTableColumns } from '../rules/rule-table-constants';
import { useApi } from '../shared/api-client/ApiClientProvider';
import { useCurrencies } from '../currency/CurrenciesContext';
import { useFeatures } from '../features/FeaturesContext';
import { useProgram } from '../loyalty/ProgramContext';
import { useRules } from '../rules/RulesContext';

export function BurningRulesTable() {
  const { burningRules: rules, fetchRules, setBurningRules } = useRules();
  const { t } = useTranslation();
  const [conflictModalIsOpen, setConflictModalIsOpen] = useState(false);
  const [conflictDialogProps, setConflictDialogProps] = useState<
    Omit<RuleConflictModalProps, 'isOpen' | 'setIsOpen'>
  >({
    ruleType: RuleType.BURN,
  });
  const { isLoading } = useRules();
  const { currencies } = useCurrencies();
  const { features } = useFeatures();
  const { write: canEdit } = features[Feature.BURNING_RULE];
  const firstActiveCurrency = currencies.find(currency => !currency.deletedAt);
  const emptyStateButtonTooltip = !canEdit
    ? t('shared.action.permission.required')
    : !firstActiveCurrency
    ? t('rule.add.no-active-currency')
    : '';

  const [isModalOpen, setIsModalOpen] = useState(false);

  const refresh = () => fetchRules(RuleType.BURN);

  const { ruleApi } = useApi();
  const { programId } = useProgram();

  const handleDragEnd = ({ source, destination }: DropResult) => {
    if (destination && source) {
      const { newRules, changes } = moveRule(rules, source.index, destination.index);
      setBurningRules(newRules);
      ruleApi
        .updateRulesPosition(programId, changes)
        .then(refresh)
        .catch(error => {
          if (error.response?.status === 409) {
            setConflictDialogProps({
              ...conflictDialogProps,
              lastModification: error.response?.data?.details,
            });
            setConflictModalIsOpen(true);
          } else {
            throw error;
          }
        });
    }
  };

  return (
    <>
      {isLoading('fetchRules') && <Skeleton />}
      <RuleConflictModal
        {...conflictDialogProps}
        setIsOpen={setConflictModalIsOpen}
        isOpen={conflictModalIsOpen}
      />
      <BurningRuleModal isOpen={isModalOpen} setIsOpen={setIsModalOpen} />
      {rules.length === 0 ? (
        <QlfPlaceholder
          icon={<QlfPlaceholderIcon icon={burningRulesIcon} />}
          primaryText={t('burning-rules.messages.no-items')}
          secondaryText={t('burning-rules.states.empty.description')}
          actions={
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '20px',
              }}
            >
              <RuleImportExportButtons ruleType="BURNING_RULE" />
              <Tooltip title={emptyStateButtonTooltip} placement="bottom">
                <div>
                  <Button
                    disabled={!canEdit || !firstActiveCurrency}
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => setIsModalOpen(true)}
                    id={'new-burning-button-id'}
                  >
                    {t('burning-rules.states.empty.create-action')}
                  </Button>
                </div>
              </Tooltip>
            </Box>
          }
        />
      ) : (
        <DetailForm>
          <DetailForm.Header>
            <RulesFilters ruleType="BURNING_RULE" onAdd={() => setIsModalOpen(true)} />
          </DetailForm.Header>
          <DetailForm.Form>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: ruleTableColumns.drag.width }}></TableCell>
                  <TableCell>{t('burning-rules.row.name')}</TableCell>
                  <TableCell>{t('burning-rules.row.reward')}</TableCell>
                  <TableCell style={{ width: ruleTableColumns.currencies.width }}>
                    {t('burning-rules.row.currencies')}
                  </TableCell>
                  <TableCell style={{ width: ruleTableColumns.actions.width }}></TableCell>
                </TableRow>
              </TableHead>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="burning-rules">
                  {({ placeholder, droppableProps, innerRef }) => (
                    <TableBody {...droppableProps} ref={innerRef}>
                      {rules.map((rule, index) => (
                        <Draggable key={rule.id} draggableId={rule.id} index={index}>
                          {({ innerRef, draggableProps, dragHandleProps }) => (
                            <BurningRuleTableRow
                              rule={rule}
                              onCloned={refresh}
                              onDeleted={refresh}
                              dragHandleProps={dragHandleProps}
                              draggableRef={innerRef}
                              draggableProps={draggableProps}
                            />
                          )}
                        </Draggable>
                      ))}
                      {placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </DragDropContext>
            </Table>
          </DetailForm.Form>
        </DetailForm>
      )}
    </>
  );
}
